'use client';

import customFetch from '@/api/fetch';
import { useTranslation } from '@/app/i18n/client';
import ExperienceCard from '@/components/experienceCard';
import { Skeleton } from '@/components/ui/skeleton';
import useGlobalState from '@/hooks/useGlobalState';

import { Experience } from '@/utils/types';
import { ArrowDown, CheckCircle, Loader2 } from 'lucide-react';

import InfoFooter from '@/components/infoFooter';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { useIsMobileResolution } from '@/hooks/useIsMobileResolution';
import { useIsStaff } from '@/hooks/useIsStaff';
import { getDateTimezone } from '@/utils/getDateTimezone';
import { use, useCallback, useEffect, useRef, useState, useTransition } from 'react';

interface AmigoMaspExperienciasClientProps {
  params: Promise<{ lng: string }>;
  initialExperiences?: Experience[];
  initialComingSoon?: Experience[];
  serverDataComplete?: boolean;
}

const EXPERIENCE_TYPE_FILTER = 'MASP_AMIGO,MASP_EVENTO_AMIGO';

export default function AmigoMaspExperienciasClient({
  params,
  initialExperiences,
  initialComingSoon,
  serverDataComplete,
}: AmigoMaspExperienciasClientProps) {
  const { lng } = use(params);
  const { t } = useTranslation(lng as string, 'amigoMaspExperiences');
  const { setState } = useGlobalState();
  const [experiences, setExperiences] = useState<Experience[]>(initialExperiences || []);
  const [take, setTake] = useState(4);
  const [skip, setSkip] = useState(0);
  const [isFetching, fetchExperiences] = useTransition();
  const [isLoading, setIsLoading] = useState(false);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [isFinished, setIsFinished] = useState(!!serverDataComplete);
  const [comingSoonExperiences, setComingSoonExperiences] = useState<Experience[]>(initialComingSoon || []);
  const isStaff = useIsStaff();
  const adminRefreshDone = useRef(false);

  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  const { isMobile, isTablet } = useIsMobileResolution();

  const getDefaultExperiences = useCallback((experiences: Experience[]) => {
    return experiences.filter(
      (experience) => new Date(experience.startOfSalesDate).getTime() <= getDateTimezone().getTime()
    );
  }, []);

  const getComingSoonExperiences = useCallback((experiences: Experience[]) => {
    return experiences.filter(
      (experience) => new Date(experience.startOfSalesDate).getTime() > getDateTimezone().getTime()
    );
  }, []);

  const loadExperiences = async () => {
    if (isLoading || !canLoadMore || isFetching) return;
    setIsLoading(true);
    setCanLoadMore(false);

    fetchExperiences(async () => {
      try {
        const response = await customFetch(
          `/experiences?take=${take}&skip=${skip}&experienceType=${EXPERIENCE_TYPE_FILTER}`
        );

        const filteredExperiences = getDefaultExperiences(response.experiences);
        const filteredComingSoon = getComingSoonExperiences(response.experiences);

        setExperiences((prev: Experience[]) => [...prev, ...filteredExperiences]);
        setComingSoonExperiences((prev: Experience[]) => [...prev, ...filteredComingSoon]);

        // Verifica se não há mais experiências ou se as filtradas acabaram
        const noMoreResults = response.experiences.length === 0;
        const allLoaded = response.total <= skip + take;
        setIsFinished(noMoreResults || allLoaded);

        setTimeout(() => {
          setCanLoadMore(true);
        }, 0);
      } catch (error: any) {
        if (error?.code === 434343) {
          setIsFinished(true);
        }
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    });
  };

  function loadMoreExperiences() {
    setSkip(skip + take);
  }

  useEffect(() => {
    setState('currentStep', 0);
    if (serverDataComplete) return;
    loadExperiences();
  }, [take, skip]);

  useEffect(() => {
    if (!isStaff || adminRefreshDone.current) return;
    adminRefreshDone.current = true;

    const refreshForAdmin = async () => {
      try {
        const response = await customFetch(`/experiences?take=${take}&skip=0&experienceType=${EXPERIENCE_TYPE_FILTER}`);
        setExperiences(getDefaultExperiences(response.experiences));
        setComingSoonExperiences(getComingSoonExperiences(response.experiences));
        setIsFinished(response.total <= take || response.experiences.length === 0);
      } catch (error) {
        console.error('[AdminRefresh] Failed to refresh experiences:', error);
      }
    };
    refreshForAdmin();
  }, [isStaff]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 750 &&
        !isFetching &&
        canLoadMore &&
        !isFinished
      ) {
        loadMoreExperiences();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isFetching, canLoadMore]);

  return (
    <>
      <div className='max-md:pb-10'>
        <div className='my-[120px] flex flex-col items-center justify-center max-sm:my-[60px]'>
          <h1
            className={`${isTablet ? 'text-[80px]' : isMobile ? 'text-[60px]' : 'text-[120px]'} text-center font-bold capitalize leading-[1.1] text-foreground max-sm:mb-0 max-sm:text-[32px]`}>
            {t('title')}
          </h1>
          <h2 className='mt-[40px] text-center text-xl font-light text-zinc-600 max-md:px-8 max-sm:mt-[10px] max-sm:text-[16px] max-sm:leading-[1.1]'>
            {t('subtitle')}
          </h2>
        </div>

        <div className='grid grid-cols-2 items-start justify-center gap-[20px] px-[20px] max-[1024px]:px-[10px] max-md:grid-cols-1'>
          {experiences.length === 0 && (isLoading || isFetching)
            ? Array.from({ length: 6 }).map((_, index) => (
                <Skeleton key={`skeleton-experience-${index}`} className='mt-7 aspect-video w-full py-32 max-md:mt-4' />
              ))
            : experiences.length > 0
              ? experiences.map((experience) => (
                  <ExperienceCard key={experience.id} lng={lng as string} experience={experience} />
                ))
              : !isLoading &&
                !isFetching && (
                  <div className='col-span-2 flex flex-col items-center justify-center py-20 text-center'>
                    <p className='text-lg text-zinc-500'>{t('noExperiences')}</p>
                  </div>
                )}
        </div>
        {!isFetching && !isFinished && experiences.length > 0 && (
          <div className='flex justify-center py-12 text-lg text-zinc-500'>
            {t('loadMoreExperiences')}{' '}
            <div className='ml-2 flex h-8 w-8 animate-bounce items-center justify-center rounded-full bg-primary/20 p-2'>
              <span className='flex h-full w-full items-center justify-center'>
                <ArrowDown className='w-6 text-primary' />
              </span>
            </div>
          </div>
        )}

        {isFinished && !isFetching && experiences.length > 0 && (
          <div className='mt-10 flex items-center justify-center py-1 text-lg max-md:text-center'>
            <CheckCircle className='mr-2 h-6 w-6 text-primary' />
            <span className='text-foreground max-sm:text-sm'>{t('allExperiencesLoaded')}</span>
          </div>
        )}
        {isFetching && !isFinished && (
          <div className='flex justify-center py-12 text-lg uppercase text-zinc-500'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        )}

        {comingSoonExperiences.length >= 3 && (
          <div className='mt-[120px] px-[20px] max-[1024px]:p-0 max-[1024px]:pl-[10px]'>
            <h2 className='w-fit text-left text-[72px] font-bold text-foreground max-[1024px]:text-[38px]'>
              {t('comingSoon')}
            </h2>

            <Carousel className='relative mt-[40px] max-sm:px-0' opts={{ loop: true, align: 'start' }} setApi={setApi}>
              <div className='absolute -top-10 right-0 flex w-full max-w-[90px] items-center justify-between gap-4 max-[1024px]:hidden'>
                <CarouselNext className='absolute bottom-0 right-0' />
                <CarouselPrevious className='absolute bottom-0 left-0' />
              </div>
              <CarouselContent className='relative'>
                {comingSoonExperiences.map((experience, index) => {
                  const aspectRatio = index === 0 ? 'aspect-[4/9]' : index === 1 ? 'aspect-[16/9]' : 'aspect-[4/4]';

                  return (
                    <CarouselItem key={experience.id} className='basis-72 lg:basis-1/3'>
                      <ExperienceCard
                        showButtons={false}
                        isCarousel={true}
                        imageClassName={`${aspectRatio} bg-zinc-300 max-[1024px]:aspect-[4/5]`}
                        key={experience.id}
                        lng={lng as string}
                        experience={experience}
                      />
                    </CarouselItem>
                  );
                })}
              </CarouselContent>
              <div className='absolute bottom-0 left-0 right-0 flex w-full items-center justify-between min-[1024px]:hidden'>
                <CarouselNext className='absolute bottom-0 right-[10px]' />
                <div className='absolute bottom-[10px] left-0 right-0 flex h-4 w-full items-center justify-center gap-2 text-center'>
                  {comingSoonExperiences.map((_, index) => {
                    return (
                      <span
                        key={index}
                        className={`h-1.5 w-1.5 bg-zinc-300 ${index + 1 === current ? 'bg-zinc-500' : ''}`}
                      />
                    );
                  })}
                </div>
                <CarouselPrevious className='absolute bottom-0 left-[10px]' />
              </div>
            </Carousel>
          </div>
        )}
      </div>

      <InfoFooter />
    </>
  );
}
